<template>
  <div class="p-4">
    <div
      class="d-flex flex-row justify-content-between align-items-center mb-3"
    >
      <h5 class="mb-0">Badges</h5>
      <b-button
        class="pl-3 pr-3 pt-1 pb-1"
        variant="primary"
        size="sm"
        @click="assignBadge"
        ><i class="fa fa-tag mr-2"></i>Assign
      </b-button>
    </div>
    <grid
      v-if="client && eoc"
      class="mb-4"
      :columns="badgeColumns"
      :data="this.eoc.badges"
      :groupFeature="false"
      :autoHeight="true"
      :columnLines="false"
      :searchFeature="true"
      :stripeFeature="false"
      :key="forceRenderMarker"
      :scrollable="allowScroll"
    ></grid>
    <b-modal
      centered
      :title="'Assign badge'"
      v-model="showBadgeModal"
      header-bg-variant="primary"
      header-text-variant="white"
      hide-footer
      size="lg"
      header-class="bg-purple"
    >
      <grid
        class="m-2"
        :columns="unassignedBadgeColumns"
        :data="unassignedBadges"
        :groupFeature="false"
        :autoHeight="true"
        :columnLines="false"
        :searchFeature="true"
        :stripeFeature="false"
      ></grid>
    </b-modal>
    <div
      class="d-flex flex-row justify-content-between align-items-center mb-3"
    >
      <h5 class="mb-0">Alerts</h5>
      <div>
        <b-button
          class="pl-3 pr-3 pt-1 pb-1 mr-2"
          variant="primary"
          size="sm"
          @click="addAlertNote"
          ><i class="fa fa-plus mr-2"></i>New</b-button
        >
      </div>
    </div>
    <grid
      v-if="client && eoc"
      class="mb-4"
      ref="grid"
      :groupFeature="false"
      :columns="alertNoteColumns"
      :autoHeight="true"
      :data="alertNoteItems"
      :columnLines="false"
      :searchFeature="true"
      :stripeFeature="false"
      @paint="listeners.paint"
      :scrollable="allowScrollAlert"
    ></grid>
    <b-modal
      centered
      ref="form-modal"
      title="Add Alert"
      header-bg-variant="primary"
      header-text-variant="white"
      @ok="submitNoteAlert"
      no-close-on-backdrop
      header-class="bg-purple"
    >
      <template v-slot:modal-footer="{ ok, cancel }">
        <b-button variant="secondary" @click="cancel()">
          Cancel
        </b-button>
        <b-button variant="primary" @click="ok()">
          Save
        </b-button>
      </template>
      <b-form-group label="Message">
        <b-form-textarea
          v-model="form.content"
          rows="3"
          max-rows="6"
          maxLength="255"
        ></b-form-textarea>
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import Grid from "../../third_party/grid/Grid.vue";

import { mapMutations, mapActions } from "vuex";
import { on } from "../../helpers/layout";
import alertNoteServer from "../../server/eocNote";
import badgesServer from "../../server/badge";
import patientServer from "../../server/patient";
import eocServer from "../../server/eoc";

export default {
  name: "EocViewBadgesNotesAlerts",
  components: {
    Grid
  },
  props: ["eocId", "clientId"],
  data() {
    const self = this;
    return {
      forceRenderMarker: 0,
      alertNoteList: [],
      badges: [],
      formId: null,
      form: {
        episodeOfCareId: this.eocId,
        content: "",
        status: "Enabled"
      },
      client: null,
      eoc: null,
      subscribed: false,
      showBadgeModal: false,
      listeners: {
        paint: paintEvent => {
          if (paintEvent.firstPaint) {
            if (!self.subscribed) {
              self.subscribed = true;
              on(self.$el, "change", ".edit-enabled-item", async target => {
                await alertNoteServer.switchStatus(target.dataset.id);
                this.getAlertsNotes();
                this.dispatchFetchClient();
                this.dispatchFetchClientAlertNotes();
                this.dispatchFetchEoc();
                this.showAlertSuccess();
              });
            }
          }
        }
      }
    };
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    ...mapActions("layout", ["showAlertSuccess"]),
    ...mapMutations("client", ["dispatchListener"]),
    async fetchData() {
      await this.getEoc();
      await this.getClient();
      this.getBadges();
      this.getAlertsNotes();
    },
    forceRerender() {
      this.forceRenderMarker += 1;
    },
    async getEoc() {
      this.eoc = await eocServer.get(this.eocId);
    },
    async getClient() {
      this.client = await patientServer.get(this.clientId);
    },
    async getAlertsNotes() {
      this.alertNoteList = await alertNoteServer.query(this.eocId);
    },
    async getBadges() {
      this.forceRerender();
      this.badges = await badgesServer.query();
    },
    async submitNoteAlert() {
      if (this.formId) await alertNoteServer.update(this.formId, this.form);
      else await alertNoteServer.create(this.form);
      this.getAlertsNotes();
      this.dispatchFetchClient();
      this.dispatchFetchClientAlertNotes();
      this.dispatchFetchEoc();
      this.showAlertSuccess();
    },
    addAlertNote() {
      this.formId = null;
      this.form = {
        episodeOfCareId: this.eocId,
        content: "",
        status: "Enabled"
      };
      this.$refs["form-modal"].show();
    },
    assignBadge() {
      this.showBadgeModal = true;
    },
    dispatchFetchClient() {
      this.dispatchListener({ listener: "fetchClient", param: this.clientId });
    },
    dispatchFetchEoc() {
      this.dispatchListener({ listener: "fetchEoc", param: "all" });
    },
    dispatchFetchClientAlertNotes() {
      this.dispatchListener({
        listener: "fetchClientAlertNotes",
        param: this.eocId
      });
    },
    exportExcel() {
      this.$refs.grid.exportExcel();
    }
  },
  computed: {
    alertNoteItems() {
      return this.alertNoteList.map(item => {
        const docHeight = 28;
        const margin = 8;
        const charsAlertsPerLine = 150;
        const alertLines = Math.ceil(item.content.length / charsAlertsPerLine);
        const breaksCount = (item.content.match(/\n/g) || [""]).length;
        const rowHeight =
          Math.max(breaksCount, alertLines) * docHeight + margin * 2;
        return {
          ...item,
          enabled: item.status == "Enabled",
          rowHeight
        };
      });
    },
    alertNoteColumns() {
      let columns = [
        {
          text: "Alert",
          field: "content",
          type: "template",
          flex: 3,
          editor: false,
          template: ({ record }) => {
            let icon = "fas fa-exclamation-circle";
            let classVal = "";
            let iconClassVal = "text-secondary";
            if (record.status == "Enabled") {
              iconClassVal = "text-danger";
              classVal = "text-danger";
            }

            return `
            <div class="text-wrap">
              <i class="alert-note-icon ${icon} ${iconClassVal}"></i> <span class="ml-2 ${classVal}">${record.content.replace(
              /\n/g,
              "<br>"
            )}</span>
            </div>
            `;
          }
        },
        {
          text: "Enabled",
          field: "status",
          type: "template",
          editor: false,
          sortable: false,
          template: ({ record }) => {
            return `<div class="custom-control custom-switch custom-switch-md">
              <input type="checkbox" ${
                record.status == "Enabled" ? "checked" : ""
              } reference="input" class="custom-control-input custom-control-input-success edit-enabled-item" data-id="${
              record.id
            }" id="enabled-item${record.id}_input">
              <label class="custom-control-label" for="enabled-item${
                record.id
              }_input"></label>
            </div>`;
          },
          flex: 1
        },
        {
          text: "",
          type: "action",
          field: "action",
          align: "right",
          flex: 1,
          actions: [
            {
              tooltip: "Edit",
              renderer: () =>
                `<img class="b-action-item" src="${
                  this.isStandalone()
                    ? `/icons/edit.svg`
                    : `/desktop/icons/edit.svg`
                }">`,
              onClick: async ({ record }) => {
                this.formId = record.id;
                const form = await alertNoteServer.get(this.formId);
                this.form = {
                  episodeOfCareId: this.eocId,
                  content: form.content,
                  status: form.status
                };
                this.$refs["form-modal"].show();
              }
            },
            {
              tooltip: "Delete",
              renderer: () =>
                `<img class="b-action-item ml-2" src="${
                  this.isStandalone()
                    ? `/icons/delete.svg`
                    : `/desktop/icons/delete.svg`
                }">`,
              onClick: ({ record }) => {
                this.$dialog
                  .confirm(
                    {
                      title: "Are you sure you want to delete this record?"
                    },
                    {
                      view: "confirm-delete"
                    }
                  )
                  .then(async () => {
                    await alertNoteServer.delete(record.id);
                    this.getAlertsNotes();
                    this.dispatchFetchClient();
                    this.dispatchFetchClientAlertNotes();
                    this.dispatchFetchEoc();
                    this.showAlertSuccess();
                  })
                  .catch(function() {});
              }
            }
          ]
        }
      ];
      return columns;
    },
    allowScroll() {
      return this.eoc.badges.length == 1 ? false : true;
    },
    badgeColumns() {
      let columns = [];

      columns.push({
        text: "Badge",
        field: "badge",
        width: 100,
        align: "center",
        editor: false,
        type: "template",
        template: ({ record }) =>
          `<div class="badge text-white" style="background-color: ${record.data.category.color}">${record.data.acronym}</div>`,
        sortable: false
      });

      columns.push({
        text: "Category",
        field: "category.name",
        flex: 1,
        editor: false,
        sortable(a, b) {
          return a.category.name.localeCompare(b.category.name);
        }
      });

      columns.push({
        text: "Name",
        field: "name",
        flex: 1,
        editor: false,
        template: ({ record }) => record.data.name
      });

      columns.push({
        text: "",
        type: "action",
        field: "action",
        align: "right",
        width: 80,
        actions: [
          {
            tooltip: "Delete",
            renderer: () =>
              `<img class="b-action-item mr-1" src="${
                this.isStandalone()
                  ? `/icons/delete.svg`
                  : `/desktop/icons/delete.svg`
              }">`,
            onClick: ({ record }) => {
              this.$dialog
                .confirm(
                  {
                    title: "Are you sure you want to remove this badge?"
                  },
                  {
                    view: "confirm-delete"
                  }
                )
                .then(async () => {
                  await eocServer.removeBadge(this.eocId, record.data.id);
                  this.showAlertSuccess();
                  await this.getEoc();
                })
                .catch(function() {});
            }
          }
        ]
      });

      return columns;
    },
    allowScrollAlert() {
      return this.alertNoteList.length == 1 ? false : true;
    },
    unassignedBadges() {
      return this.badges.filter(
        b => !this.eoc.badges.map(u => u.id).includes(b.id)
      );
    },
    unassignedBadgeColumns() {
      let columns = [];

      columns.push({
        text: "Badge",
        field: "badge",
        width: 100,
        align: "center",
        editor: false,
        type: "template",
        template: ({ record }) =>
          `<div class="badge text-white" style="background-color: ${record.data.category.color}">${record.data.acronym}</div>`
      });

      columns.push({
        text: "Category",
        field: "category",
        flex: 1,
        editor: false,
        type: "template",
        template: ({ record }) => record.data.category.name
      });

      columns.push({
        text: "Name",
        field: "name",
        flex: 1,
        editor: false
      });

      columns.push({
        text: "",
        type: "action",
        field: "action",
        align: "right",
        width: 80,
        actions: [
          {
            tooltip: "Assign",
            renderer: () =>
              `<span class="b-action-item action-link-text document-permissions text-primary">Assign</span>`,
            onClick: async ({ record }) => {
              await eocServer.addBadge(this.eocId, record.data.id);
              this.eoc.badges.push(record.originalData);
              this.dispatchFetchEoc();

              if (this.unassignedBadges.length < 1) {
                this.showBadgeModal = false;
              }
            }
          }
        ]
      });

      return columns;
    }
  },
  watch: {
    showBadgeModal() {
      this.getBadges();
    }
  }
};
</script>
